/**
 * Utilidades para manejo de JWT
 */

export interface JWTPayload {
  id?: number
  role?: number
  usuario?: string
  email?: string
  iat?: number
  exp?: number
}

/**
 * Decodifica un token JWT sin verificar la firma
 * ⚠️ ADVERTENCIA: Esto solo decodifica, NO valida la firma. La validación debe hacerse en el backend.
 */
export function decodeJWT(token: string): JWTPayload | null {
  try {
    const parts = token.split('.')
    if (parts.length !== 3) {
      return null
    }

    const base64Url = parts[1]
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    )
    return JSON.parse(jsonPayload)
  } catch {
    return null
  }
}

/**
 * Verifica si un token JWT ha expirado
 */
export function isTokenExpired(token: string): boolean {
  const decoded = decodeJWT(token)
  if (!decoded || !decoded.exp) {
    return true // Si no tiene exp, considerarlo expirado por seguridad
  }

  // exp está en segundos, Date.now() está en milisegundos
  const expirationTime = decoded.exp * 1000
  const currentTime = Date.now()
  
  // Agregar un buffer de 5 minutos antes de la expiración real
  const bufferTime = 5 * 60 * 1000 // 5 minutos en milisegundos
  
  return currentTime >= (expirationTime - bufferTime)
}

/**
 * Obtiene el tiempo restante hasta la expiración del token en milisegundos
 */
export function getTokenTimeRemaining(token: string): number {
  const decoded = decodeJWT(token)
  if (!decoded || !decoded.exp) {
    return 0
  }

  const expirationTime = decoded.exp * 1000
  const currentTime = Date.now()
  const remaining = expirationTime - currentTime

  return remaining > 0 ? remaining : 0
}

/**
 * Verifica si un token es válido (no expirado y tiene estructura correcta)
 */
export function isTokenValid(token: string): boolean {
  if (!token || typeof token !== 'string') {
    return false
  }

  const decoded = decodeJWT(token)
  if (!decoded) {
    return false
  }

  // Verificar que tenga los campos mínimos necesarios
  if (decoded.id === undefined || decoded.role === undefined) {
    return false
  }

  // Verificar que no esté expirado
  if (isTokenExpired(token)) {
    return false
  }

  return true
}

/**
 * Extrae información del usuario del token
 */
export function getUserFromToken(token: string): { id: number; role: number; name?: string; email?: string } | null {
  const decoded = decodeJWT(token)
  if (!decoded || decoded.id === undefined || decoded.role === undefined) {
    return null
  }

  return {
    id: decoded.id,
    role: decoded.role,
    name: decoded.usuario,
    email: decoded.email,
  }
}


